import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  Box,
  CircularProgress,
} from "@mui/material";
import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Button from "../theme/components/Button.jsx";
import { backendGet } from "../services/dataService.js";
import { SEARCH_WORD_KEY } from "./tables/utils/tableUtils.js";
import PropTypes from "prop-types";

/**
 * NewDamageClaimDialog Component
 * Reusable dialog for creating a new damage claim by selecting an object/contract
 * @param {boolean} open - Whether the dialog is open
 * @param {function} onClose - Callback when dialog is closed
 * @param {function} onSelect - Optional callback when an object is selected (if not provided, navigates to claim creation page)
 * @param {string} title - Optional custom title for the dialog
 */
export default function NewDamageClaimDialog({
  open,
  onClose,
  onSelect,
  title,
}) {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const navigate = useNavigate();

  // Debounced search function
  const debouncedSearch = useCallback(async (query) => {
    if (query.length < 3) {
      setSearchResults([]);
      setSearchError(null);
      return;
    }

    setIsLoading(true);
    setSearchError(null);

    try {
      const results = await backendGet(`data/objekts`, {
        label: "Objektsuche",
        setResult: (res) => {
          setSearchResults(res.data);
        },
        options: {
          params: {
            api_version: "v2",
            filter: {
              [SEARCH_WORD_KEY]: query,
            },
            include: ["sparten", "vertrag"],
          },
        },
      });

      if (results === null) {
        setSearchError("Fehler bei der Suche");
      }
    } catch (error) {
      setSearchError("Fehler bei der Suche");
      console.error("Search error:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Handle search with debouncing
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      debouncedSearch(searchQuery);
    }, 400); // 400ms debounce

    return () => clearTimeout(timeoutId);
  }, [searchQuery, debouncedSearch]);

  // Handle object selection
  const handleObjectSelect = (object) => {
    onClose();
    if (onSelect) {
      onSelect(object);
    } else {
      navigate(`/schaeden/neu/${object.vertrag_id}/${object.id}`);
    }
  };

  // Clear search when dialog closes
  const handleClose = () => {
    setSearchQuery("");
    setSearchResults([]);
    setSearchError(null);
    onClose();
  };

  // Render list content based on state
  const renderListContent = () => {
    if (isLoading) {
      return (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={24} />
        </Box>
      );
    }

    if (searchQuery.length < 3) {
      return (
        <Box sx={{ textAlign: "center", py: 4, color: "text.secondary" }}>
          <Typography variant="body2">
            Geben Sie mindestens 3 Zeichen ein, um zu suchen...
          </Typography>
        </Box>
      );
    }

    if (searchError) {
      return (
        <Box sx={{ textAlign: "center", py: 4, color: "error.main" }}>
          <Typography variant="body2">{searchError}</Typography>
        </Box>
      );
    }

    if (searchResults.length === 0 && searchQuery.length >= 3) {
      return (
        <Box sx={{ textAlign: "center", py: 4, color: "text.secondary" }}>
          <Typography variant="body2">
            Keine Objekte für "{searchQuery}" gefunden
          </Typography>
        </Box>
      );
    }

    return (
      <List disablePadding>
        {searchResults.map((object) => (
          <ListItem key={object.id} disablePadding divider>
            <ListItemButton onClick={() => handleObjectSelect(object)}>
              <ListItemText
                primary={
                  object.bezeichnung ||
                  `${object.strasse} ${object.hausnr || ""}`.trim() ||
                  "Objekt"
                }
                secondary={
                  <>
                    {object.sparten.map((s) => s.sparte).join(", ")} {" | "}
                    Vertrag {object?.vertrag?.vertragsnummer} (
                    {object?.vertrag?.gesellschaft})
                  </>
                }
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    );
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>{title || "Neuen Schaden anlegen"}</DialogTitle>
      <DialogContent>
        <Typography variant="body1">
          Wählen Sie das Betreffende Objekt mit dem zugehörigen Vertrag aus.
        </Typography>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
          {/* Search field */}
          <TextField
            fullWidth
            placeholder="Vertrag oder Objekt suchen..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            size="small"
            autoFocus
          />

          {/* Search results */}
          <Box
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
              maxHeight: 300,
              overflow: "auto",
            }}
          >
            {renderListContent()}
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button variant="tertiary" onClick={handleClose}>
          Abbrechen
        </Button>
      </DialogActions>
    </Dialog>
  );
}

NewDamageClaimDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSelect: PropTypes.func,
  title: PropTypes.string,
};
